import {
  Component,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  HostListener,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from "@angular/core";
import { trigger, style, transition, animate, AnimationEvent } from "@angular/animations";
import { SvgPaths } from "@nexu-library/utilities";
import { BottomSheetButton, BottomSheetRow } from "../types/bottom-sheet.types";

@Component({
  selector: "ds-bottom-sheet",
  templateUrl: "./ds-bottom-sheet.component.html",
  styleUrls: ["./ds-bottom-sheet.component.scss"],
  animations: [
    trigger("slideUp", [
      transition(":enter", [
        style({ transform: "translateY(100%)" }),
        animate("300ms ease-out", style({ transform: "translateY(0)" })),
      ]),
      transition(":leave", [
        style({ transform: "translateY(0)" }),
        animate("200ms ease-in", style({ transform: "translateY(100%)" })),
      ]),
    ]),
    trigger("fadeIn", [
      transition(":enter", [style({ opacity: 0 }), animate("300ms ease-out", style({ opacity: 1 }))]),
      transition(":leave", [style({ opacity: 1 }), animate("200ms ease-in", style({ opacity: 0 }))]),
    ]),
  ],
})
export class DsBottomSheetComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() title?: string;
  @Input() buttons: BottomSheetButton[] = [];
  @Input() service = false;
  @Input() src: string = "";
  @Input() description: string = "";
  @Input() fullScreen: boolean = false;
  @Input() closeOnAction: boolean = true;
  @Input() persistent: boolean = false;
  @Input() buttonsAlign: "center" | "start" | "end" = "center";
  /**
   * Rows with optional priority. Same priority: higher = more above (mobile), more to the right (desktop).
   * When priority is omitted, row index is used.
   */
  @Input() rows?: BottomSheetRow[];

  isVisible = true;
  private isClosing = false;

  @ViewChild("descriptionElement", { static: false }) descriptionElement?: ElementRef<HTMLElement>;
  @ViewChild("resizeHandle", { static: false }) resizeHandle?: ElementRef<HTMLElement>;
  @ViewChild("bottomSheet", { static: false }) bottomSheet?: ElementRef<HTMLElement>;

  line = SvgPaths.line;

  private readonly min_description_height = 110;
  descriptionHeight: number = 110;
  currentDescriptionHeight: number = 110;
  maxLines: number = 5;
  showResizeHandle: boolean = false;

  private isResizing = false;
  private startY = 0;
  private startHeight = 0;

  private boundResizeMove = this.onResizeMove.bind(this);
  private boundResizeEnd = this.onResizeEnd.bind(this);

  // Persistent action bars are fixed to the viewport bottom and float over the page. They publish their
  // measured height as a global CSS variable so any scroll container can reserve space and never hide
  // content behind the sheet. The gap adds a minimum visual separation above the sheet.
  private static readonly RESERVED_SPACE_VARIABLE = "--ds-bottom-sheet-height";
  private readonly reservedSpaceGap = 16;
  private reservedSpaceObserver?: ResizeObserver;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes["description"] && this.description) {
      setTimeout(() => this.calculateDescriptionHeight(), 0);
    }
  }

  ngAfterViewInit() {
    if (this.description) {
      setTimeout(() => this.calculateDescriptionHeight(), 0);
    }
    setTimeout(() => this.setupReservedSpace(), 0);
  }

  @HostListener("window:resize", ["$event"])
  onResize() {
    if (this.description) {
      this.calculateDescriptionHeight();
    }
    this.updateReservedSpace();
  }

  // Only persistent service sheets (action bars) reserve space; modal sheets have an overlay and must not.
  private setupReservedSpace(): void {
    if (!this.service || !this.persistent || !this.bottomSheet?.nativeElement) return;

    this.updateReservedSpace();

    if (typeof ResizeObserver !== "undefined") {
      this.reservedSpaceObserver = new ResizeObserver(() => this.updateReservedSpace());
      this.reservedSpaceObserver.observe(this.bottomSheet.nativeElement);
    }
  }

  private updateReservedSpace(): void {
    if (!this.service || !this.persistent) return;

    const element = this.bottomSheet?.nativeElement;
    if (!element) return;

    const height = element.getBoundingClientRect().height;
    if (height > 0) {
      document.documentElement.style.setProperty(
        DsBottomSheetComponent.RESERVED_SPACE_VARIABLE,
        `${Math.ceil(height) + this.reservedSpaceGap}px`
      );
    }
  }

  private clearReservedSpace(): void {
    this.reservedSpaceObserver?.disconnect();
    this.reservedSpaceObserver = undefined;
    document.documentElement.style.removeProperty(DsBottomSheetComponent.RESERVED_SPACE_VARIABLE);
  }

  private calculateDescriptionHeight(): void {
    if (this.descriptionElement?.nativeElement) {
      const element = this.descriptionElement.nativeElement;
      const originalDisplay = element.style.display;
      element.style.display = "block";
      const altura = element.scrollHeight || element.clientHeight;
      element.style.display = originalDisplay || "";

      if (altura > 0) {
        this.descriptionHeight = altura;
        if (this.currentDescriptionHeight > this.descriptionHeight) {
          this.currentDescriptionHeight = this.descriptionHeight;
        }
        this.showResizeHandle = this.descriptionHeight > this.min_description_height;
        this.updateMaxLines();
        this.cdr.detectChanges();
      }
    }
  }

  private getCssVariableValue(variableName: string): number {
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue(variableName).trim();
    return parseFloat(value) || 0;
  }

  private updateMaxLines(): void {
    const lineHeight = this.getCssVariableValue("--line_height_04");
    if (lineHeight > 0) {
      this.maxLines = Math.floor(this.currentDescriptionHeight / lineHeight);
      this.cdr.detectChanges();
    }
  }

  onResizeStart(event: MouseEvent | TouchEvent): void {
    if (!this.descriptionElement?.nativeElement) return;

    event.preventDefault();
    this.isResizing = true;

    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    this.startY = clientY;
    this.startHeight = this.currentDescriptionHeight;

    document.addEventListener("mousemove", this.boundResizeMove);
    document.addEventListener("mouseup", this.boundResizeEnd);
    document.addEventListener("touchmove", this.boundResizeMove);
    document.addEventListener("touchend", this.boundResizeEnd);
  }

  private onResizeMove(event: MouseEvent | TouchEvent): void {
    if (!this.isResizing || !this.descriptionElement?.nativeElement) return;

    event.preventDefault();

    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    const deltaY = clientY - this.startY;

    let newHeight = this.startHeight - deltaY;
    if (this.service && this.bottomSheet?.nativeElement) {
      const bottomSheet = this.bottomSheet.nativeElement;
      const viewportHeight = window.innerHeight;
      const bottomSheetHeight = bottomSheet.getBoundingClientRect().height;
      if (bottomSheetHeight >= viewportHeight && deltaY < 0) {
        return;
      }
    }

    newHeight = Math.max(this.min_description_height, Math.min(newHeight, this.descriptionHeight));

    this.currentDescriptionHeight = newHeight;
    this.updateMaxLines();
    this.cdr.detectChanges();
  }

  private onResizeEnd(event: MouseEvent | TouchEvent): void {
    if (!this.isResizing) return;

    event.preventDefault();
    this.isResizing = false;

    document.removeEventListener("mousemove", this.boundResizeMove);
    document.removeEventListener("mouseup", this.boundResizeEnd);
    document.removeEventListener("touchmove", this.boundResizeMove);
    document.removeEventListener("touchend", this.boundResizeEnd);
  }

  ngOnDestroy(): void {
    document.removeEventListener("mousemove", this.boundResizeMove);
    document.removeEventListener("mouseup", this.boundResizeEnd);
    document.removeEventListener("touchmove", this.boundResizeMove);
    document.removeEventListener("touchend", this.boundResizeEnd);
    this.clearReservedSpace();
  }

  closeFn?: () => void;

  onAnimationDone(event: AnimationEvent) {
    if (event.triggerName === "slideUp" && event.toState === "void" && this.isClosing) {
      this.isClosing = false;
      this.closeFn?.();
    }
  }

  close() {
    if (this.service && !this.isClosing) {
      this.isClosing = true;
      this.isVisible = false;
      this.clearReservedSpace();
    }
  }

  onClick(action: () => void) {
    action();
    if (this.closeOnAction) {
      this.close();
    }
  }
}
